import { Copy, LifeBuoy, Mail } from 'lucide-react';

import type { PaymentInfo } from '@/api/payments';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { upiQrUrl } from '@/lib/upiQr';

function CopyField({ label, value }: { label: string; value: string }) {
  if (!value) return null;

  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-secondary/50 px-3 py-2">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate font-mono text-sm">{value}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={() => {
          void navigator.clipboard.writeText(value);
          toast({ title: 'Copied' });
        }}
        aria-label={`Copy ${label}`}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export function PaymentMethodCard({ info }: { info: PaymentInfo }) {
  const qr = info.qrImageUrl || (info.upiId ? upiQrUrl(info.upiId, info.upiName || 'Farmacia', 180) : null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">How to pay</CardTitle>
        {info.instructions && <p className="text-xs text-muted-foreground">{info.instructions}</p>}
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-[auto,1fr]">
        {qr && (
          <img src={qr} alt="Payment QR code" width={160} height={160} className="mx-auto rounded-lg border-2 border-border sm:mx-0" />
        )}

        <div className="space-y-2">
          <CopyField label="UPI ID" value={info.upiId} />
          <CopyField label="Bank account" value={info.bankAccountNo} />
          <CopyField label="IFSC" value={info.bankIfsc} />
          {info.bankName && (
            <p className="px-1 text-xs text-muted-foreground">
              {info.bankAccountName} · {info.bankName}
              {info.bankBranch && ` · ${info.bankBranch}`}
            </p>
          )}

          <div className="flex flex-wrap gap-3 pt-1 text-xs">
            {info.whatsappNumber && (
              <a href={`https://wa.me/${info.whatsappNumber.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                <LifeBuoy className="h-3.5 w-3.5" /> WhatsApp support
              </a>
            )}
            {info.supportEmail && (
              <a href={`mailto:${info.supportEmail}`} className="flex items-center gap-1 text-primary hover:underline">
                <Mail className="h-3.5 w-3.5" /> {info.supportEmail}
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
